import React, { useState, useEffect } from 'react';
import { emailApi, Domain, EmailEntry, EmailTemplate, Analytics } from '../utils/emailApi';
import { useUser } from '../contexts/UserContext';

export const EmailManagerV2: React.FC = () => {
  const { user, logout } = useUser();
  const [domains, setDomains] = useState<Domain[]>([]);
  const [emails, setEmails] = useState<EmailEntry[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form states
  const [newEmail, setNewEmail] = useState({ email: '', url: '', domain_id: '', description: '', tags: '' });
  const [newDomain, setNewDomain] = useState({ domain: '', default_email: '', is_catch_all: false, description: '' });
  const [showDomainForm, setShowDomainForm] = useState(false);
  const [showTemplateGenerator, setShowTemplateGenerator] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [selectedDomainForGeneration, setSelectedDomainForGeneration] = useState<number | null>(null);

  // Load data on component mount and when user changes
  useEffect(() => {
    if (user) {
      emailApi.setUserId(user.id);
      loadAllData();
    }
  }, [user]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [domainsData, emailsData, templatesData, analyticsData] = await Promise.all([
        emailApi.getDomains(),
        emailApi.getEmailEntries({ limit: 100 }),
        emailApi.getTemplates(),
        emailApi.getAnalytics()
      ]);
      
      setDomains(domainsData);
      setEmails(emailsData);
      setTemplates(templatesData);
      setAnalytics(analyticsData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const addDomain = async () => {
    if (!newDomain.domain || !newDomain.default_email) return;
    
    try {
      await emailApi.createDomain({
        domain: newDomain.domain,
        default_email: newDomain.default_email,
        is_catch_all: newDomain.is_catch_all,
        description: newDomain.description || undefined
      });
      
      setNewDomain({ domain: '', default_email: '', is_catch_all: false, description: '' });
      setShowDomainForm(false);
      await loadAllData(); // Refresh data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create domain');
    }
  };

  const deleteDomain = async (id: number) => {
    try {
      await emailApi.deleteDomain(id);
      await loadAllData(); // Refresh data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete domain');
    }
  };

  const addEmail = async () => {
    if (!newEmail.email || !newEmail.url) return;
    
    try {
      const tags = newEmail.tags ? newEmail.tags.split(',').map(t => t.trim()).filter(t => t) : [];
      
      await emailApi.createEmailEntry({
        email: newEmail.email,
        url: newEmail.url,
        domain_id: newEmail.domain_id ? parseInt(newEmail.domain_id) : undefined,
        description: newEmail.description || undefined,
        tags
      });
      
      setNewEmail({ email: '', url: '', domain_id: '', description: '', tags: '' });
      await loadAllData(); // Refresh data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create email entry');
    }
  };

  const deleteEmail = async (id: number) => {
    try {
      await emailApi.deleteEmailEntry(id);
      await loadAllData(); // Refresh data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete email entry');
    }
  };

  const copyToClipboard = async (text: string, emailId: number) => {
    try {
      await navigator.clipboard.writeText(text);
      await emailApi.copyEmail(emailId); // Log the copy action
      await loadAllData(); // Refresh to update usage count
    } catch (err) {
      console.error('Failed to copy or log action:', err);
    }
  };

  const generateEmailFromTemplate = async () => {
    if (!selectedTemplate || !selectedDomainForGeneration) return;

    try {
      const result = await emailApi.generateEmail(selectedTemplate, selectedDomainForGeneration);
      await navigator.clipboard.writeText(result.email);
      alert(`Generated and copied: ${result.email}`);
      setShowTemplateGenerator(false);
      setSelectedTemplate(null);
      setSelectedDomainForGeneration(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate email');
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading email manager...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Email Manager v2</h1>
          <p className="text-gray-600">Full-stack email management with Cloudflare D1 persistence</p>
        </div>
        {user && (
          <div className="text-right">
            <p className="text-sm text-gray-600">Welcome, {user.name}</p>
            <p className="text-xs text-gray-500">{user.email}</p>
            <button 
              onClick={logout}
              className="text-xs text-blue-500 hover:text-blue-700 mt-1"
            >
              Logout
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
          <button 
            onClick={() => setError(null)}
            className="mt-2 text-sm text-red-600 hover:text-red-800"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Analytics Dashboard */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-blue-600">{analytics.summary.total_emails}</div>
            <div className="text-sm text-gray-600">Total Emails</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-green-600">{analytics.summary.total_domains}</div>
            <div className="text-sm text-gray-600">Domains</div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-purple-600">{analytics.summary.catch_all_domains}</div>
            <div className="text-sm text-gray-600">Catch-all Domains</div>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-orange-600">{templates.length}</div>
            <div className="text-sm text-gray-600">Templates</div>
          </div>
        </div>
      )}

      {/* Template Generator */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Quick Generate</h2>
          <button
            onClick={() => setShowTemplateGenerator(!showTemplateGenerator)}
            className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600"
          >
            Generate from Template
          </button>
        </div>

        {showTemplateGenerator && (
          <div className="p-4 bg-purple-50 rounded border-l-4 border-purple-500">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <select
                value={selectedTemplate || ''}
                onChange={(e) => setSelectedTemplate(e.target.value ? parseInt(e.target.value) : null)}
                className="border rounded px-3 py-2"
              >
                <option value="">Select template...</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name} - {template.pattern}
                  </option>
                ))}
              </select>
              <select
                value={selectedDomainForGeneration || ''}
                onChange={(e) => setSelectedDomainForGeneration(e.target.value ? parseInt(e.target.value) : null)}
                className="border rounded px-3 py-2"
              >
                <option value="">Select domain...</option>
                {domains.map((domain) => (
                  <option key={domain.id} value={domain.id}>
                    {domain.domain} {domain.is_catch_all && '(catch-all)'}
                  </option>
                ))}
              </select>
            </div>
            <div className="mt-4 flex gap-2">
              <button
                onClick={generateEmailFromTemplate}
                disabled={!selectedTemplate || !selectedDomainForGeneration}
                className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600 disabled:bg-gray-300"
              >
                Generate & Copy
              </button>
              <button
                onClick={() => setShowTemplateGenerator(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Domain Management */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Domains</h2>
          <button
            onClick={() => setShowDomainForm(!showDomainForm)}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Add Domain
          </button>
        </div>

        {showDomainForm && (
          <div className="mb-4 p-4 bg-blue-50 rounded border-l-4 border-blue-500">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="Domain (e.g., oh.mom)"
                value={newDomain.domain}
                onChange={(e) => setNewDomain({ ...newDomain, domain: e.target.value })}
                className="border rounded px-3 py-2"
              />
              <input
                type="email"
                placeholder="Default email"
                value={newDomain.default_email}
                onChange={(e) => setNewDomain({ ...newDomain, default_email: e.target.value })}
                className="border rounded px-3 py-2"
              />
              <input
                type="text"
                placeholder="Description (optional)"
                value={newDomain.description}
                onChange={(e) => setNewDomain({ ...newDomain, description: e.target.value })}
                className="border rounded px-3 py-2"
              />
            </div>
            <label className="flex items-center mt-2">
              <input
                type="checkbox"
                checked={newDomain.is_catch_all}
                onChange={(e) => setNewDomain({ ...newDomain, is_catch_all: e.target.checked })}
                className="mr-2"
              />
              Catch-all domain
            </label>
            <div className="mt-4 flex gap-2">
              <button
                onClick={addDomain}
                className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
              >
                Add Domain
              </button>
              <button
                onClick={() => setShowDomainForm(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {domains.map((domain) => (
            <div key={domain.id} className="flex items-center justify-between bg-gray-50 p-3 rounded">
              <div className="flex-1">
                <div className="font-medium">{domain.domain}</div>
                <div className="text-sm text-gray-600">
                  Default: {domain.default_email}
                  {domain.is_catch_all && <span className="ml-2 text-green-600">(Catch-all)</span>}
                  {domain.description && <span className="ml-2 text-gray-500">- {domain.description}</span>}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => deleteDomain(domain.id)}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Email Entry Form */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-xl font-semibold mb-4">Add Email Entry</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            type="email"
            placeholder="Email address"
            value={newEmail.email}
            onChange={(e) => setNewEmail({ ...newEmail, email: e.target.value })}
            className="border rounded px-3 py-2"
          />
          <input
            type="url"
            placeholder="Website URL"
            value={newEmail.url}
            onChange={(e) => setNewEmail({ ...newEmail, url: e.target.value })}
            className="border rounded px-3 py-2"
          />
          <select
            value={newEmail.domain_id}
            onChange={(e) => setNewEmail({ ...newEmail, domain_id: e.target.value })}
            className="border rounded px-3 py-2"
          >
            <option value="">Select domain (optional)</option>
            {domains.map((domain) => (
              <option key={domain.id} value={domain.id}>
                {domain.domain}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Tags (comma-separated)"
            value={newEmail.tags}
            onChange={(e) => setNewEmail({ ...newEmail, tags: e.target.value })}
            className="border rounded px-3 py-2"
          />
          <input
            type="text"
            placeholder="Description (optional)"
            value={newEmail.description}
            onChange={(e) => setNewEmail({ ...newEmail, description: e.target.value })}
            className="border rounded px-3 py-2 md:col-span-2"
          />
        </div>
        <button
          onClick={addEmail}
          className="mt-4 bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600"
        >
          Add Email
        </button>
      </div>

      {/* Email List */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-xl font-semibold mb-4">Email Entries ({emails.length})</h2>
        {emails.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No email entries yet. Add one above!</p>
        ) : (
          <div className="space-y-3">
            {emails.map((email) => (
              <div key={email.id} className="flex items-center justify-between bg-gray-50 p-4 rounded">
                <div className="flex-1">
                  <div className="font-medium text-blue-600">{email.email}</div>
                  <div className="text-sm text-gray-600">{email.url}</div>
                  <div className="text-xs text-gray-500 flex gap-4">
                    <span>Domain: {email.domain_name || 'Unknown'}</span>
                    <span>Used: {email.usage_count} times</span>
                    <span>Created: {new Date(email.created_at).toLocaleDateString()}</span>
                  </div>
                  {email.tags.length > 0 && (
                    <div className="flex gap-1 mt-1">
                      {email.tags.map((tag, index) => (
                        <span key={index} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => copyToClipboard(email.email, email.id)}
                    className="text-blue-500 hover:text-blue-700 text-sm"
                  >
                    Copy
                  </button>
                  <button
                    onClick={() => window.open(email.url, '_blank')}
                    className="text-green-500 hover:text-green-700 text-sm"
                  >
                    Visit
                  </button>
                  <button
                    onClick={() => deleteEmail(email.id)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};